import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { useSupervisedData } from "@/hooks/useSupervisedData";
import { useSupervisedCourseStats } from "@/hooks/useSupervisedCourseStats";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TrainingResultsChart from "@/components/training/results/TrainingResultsChart";
import TrainingResultsTable from "@/components/training/results/TrainingResultsTable";
import TrainingStatistics from "@/components/training/results/TrainingStatistics";
import CourseStatsFilters from "@/components/course-stats/CourseStatsFilters";
import PerformanceSummaryCards from "@/components/course-stats/PerformanceSummaryCards";
import BestPerformanceSection from "@/components/course-stats/BestPerformanceSection";
import AdvancedStatsCards from "@/components/course-stats/AdvancedStatsCards";
import ScoreEvolutionChart from "@/components/course-stats/ScoreEvolutionChart";
import PrecisionEvolutionChart from "@/components/course-stats/PrecisionEvolutionChart";
import RoundsHistoryTable from "@/components/course-stats/RoundsHistoryTable";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { getTranslatedTestName } from "@/utils/trainingResults/translationUtils";
import { RefreshCw } from "lucide-react";

const TrainingResults = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation(['training', 'course-stats']);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [selectedTrainee, setSelectedTrainee] = useState<string>("");
  const [supervisedTrainees, setSupervisedTrainees] = useState<any[]>([]);
  const [isCoach, setIsCoach] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const {
    levelTestResults,
    puttingResults,
    swingTendencies,
    loading: dataLoading,
    refetch
  } = useSupervisedData(selectedTrainee || undefined);

  const {
    filteredStats: courseStats,
    isLoading: courseStatsLoading,
    filters: courseFilters,
    setFilters: setCourseFilters,
    courses
  } = useSupervisedCourseStats(selectedTrainee || undefined);

  useEffect(() => {
    const checkAuthAndLoadTrainees = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Vérifier si l'utilisateur est coach
      const { data: coachData } = await supabase
        .from("coaches")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!coachData) {
        toast({
          variant: "destructive",
          title: t('results.supervision.accessDenied'),
          description: t('results.supervision.coachOnly'),
        });
        navigate("/");
        return;
      }

      setIsCoach(true);

      // Récupérer la liste des stagiaires supervisés avec une approche différente
      try {
        // D'abord récupérer les permissions actives
        const { data: permissions, error: permissionsError } = await supabase
          .from("data_sharing_permissions")
          .select("trainee_user_id")
          .eq("coach_id", coachData.id)
          .eq("is_active", true);

        if (permissionsError) throw permissionsError;

        if (!permissions || permissions.length === 0) {
          setSupervisedTrainees([]);
          setLoading(false);
          return;
        }

        // Ensuite récupérer les profils des stagiaires (dédupliquer les IDs)
        const traineeIds = [...new Set(permissions.map(p => p.trainee_user_id))];
        // Récupérer les informations de profil sécurisées pour chaque stagiaire
        const traineeProfiles = await Promise.all(
          traineeIds.map(async (traineeId) => {
            try {
              const { data, error } = await supabase.rpc('get_trainee_safe_profile', {
                trainee_id: traineeId
              });
              
              if (error) {
                console.error(`Erreur récupération profil ${traineeId}:`, error);
                return null;
              }
              
              return data?.[0] ? {
                user_id: traineeId,
                username: data[0].username || "Utilisateur inconnu"
              } : null;
            } catch (error) {
              console.error(`Erreur accès profil ${traineeId}:`, error);
              return null;
            }
          })
        );

        const trainees = traineeProfiles.filter(Boolean);

        setSupervisedTrainees(trainees);
        
        // Sélectionner automatiquement le premier stagiaire s'il y en a
        if (trainees.length > 0) {
          setSelectedTrainee(trainees[0].user_id);
        }
      } catch (error: any) {
        console.error("Erreur lors du chargement des stagiaires:", error);
        toast({
          variant: "destructive",
          title: t('common.error'),
          description: t('results.supervision.loadingError'),
        });
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndLoadTrainees();
  }, [navigate, toast]);

  const formatResults = () => {
    const combinedResults: any[] = [];

    // Ajouter les résultats de level_tests_results avec traductions
    levelTestResults.forEach(result => {
      const testId = result.test_id;
      const testLevel = result.level?.toString();
      const translatedName = getTranslatedTestName(testId, testLevel, t);
      
      combinedResults.push({
        id: result.id,
        test_date: result.test_date,
        test_name: translatedName,
        test_id: testId,
        test_level: testLevel,
        points: result.points,
        index: result.index || 0
      });
    });

    // Ajouter les résultats de putting_test_results
    puttingResults.forEach(result => {
      combinedResults.push({
        id: result.id,
        test_date: result.test_date,
        test_name: `Test de putting (Situation ${result.situation_id})`,
        test_id: `putting-${result.situation_id}`,
        test_level: result.test_level?.toString(),
        points: result.points,
        index: result.index || 0
      });
    });

    // Définir l'ordre de priorité des exercices
    const exerciseOrder = {
      'putting': 1,
      'chipping': 2,
      'wedging': 3,
      'bunker': 4,
      'iron': 5,
      'fairwaywood': 6,
      'driver': 7
    };

    // Fonction pour déterminer le type d'exercice
    const getExerciseType = (result: any) => {
      const testName = (result.test_name || '').toLowerCase();
      const testId = (result.test_id || '').toLowerCase();
      
      if (testId.includes('putt') || testName.includes('putt')) return 'putting';
      if (testId.includes('chip') || testName.includes('chip')) return 'chipping';
      if (testId.includes('wedg') || testName.includes('wedg')) return 'wedging';
      if (testId.includes('bunker') || testName.includes('bunker')) return 'bunker';
      if (testId.includes('iron') || testName.includes('fer')) return 'iron';
      if (testId.includes('fairwaywood') || testName.includes('bois')) return 'fairwaywood';
      if (testId.includes('driver') || testName.includes('driver')) return 'driver';
      
      return 'other';
    };

    // Trier les résultats par type d'exercice puis par date
    return combinedResults.sort((a, b) => {
      const typeA = getExerciseType(a);
      const typeB = getExerciseType(b);
      
      // D'abord trier par type d'exercice
      const orderA = exerciseOrder[typeA] || 99;
      const orderB = exerciseOrder[typeB] || 99;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // Ensuite trier par date (plus récent en premier)
      return new Date(b.test_date).getTime() - new Date(a.test_date).getTime();
    });
  };

  const getFilteredResults = () => {
    const allResults = formatResults();
    // Filtrer pour ne garder que les niveaux 1, 2, 3 (exclure N/A et null qui sont les tests de Pelz)
    const trainingResults = allResults.filter(result => 
      result.test_level === "1" || result.test_level === "2" || result.test_level === "3"
    );
    if (!selectedLevel) return trainingResults;
    return trainingResults.filter(result => result.test_level === selectedLevel);
  };

  const getResultsByLevel = (level: string) => {
    return formatResults().filter(result => result.test_level === level);
  };

  const getTestTypes = () => {
    const results = formatResults();
    return [...new Set(results.map(r => r.test_name || r.test_id))];
  };

  const getChartDataByTest = () => {
    const results = formatResults();
    const groupedByTest: Record<string, any[]> = {};
    
    results.forEach(result => {
      const testName = result.test_name || result.test_id;
      if (!groupedByTest[testName]) {
        groupedByTest[testName] = [];
      }
      groupedByTest[testName].push({
        date: new Date(result.test_date).toLocaleDateString(),
        points: result.points,
        level: result.test_level
      });
    });

    return groupedByTest;
  };

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
        <NavigationMenu />
        <div className="container mx-auto p-8">
          <div className="flex justify-center items-center h-64">
            {t('buttons.loading')}
          </div>
        </div>
      </div>
    );
  }

  if (!isCoach) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
        <NavigationMenu />
        <div className="container mx-auto p-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">{t('results.supervision.accessDenied')}</h1>
            <p>{t('results.supervision.coachOnly')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (supervisedTrainees.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
        <NavigationMenu />
        <div className="container mx-auto p-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-golf-800 mb-4">{t('results.supervision.title')}</h1>
            <p className="text-gray-600">{t('results.supervision.noTrainees')}</p>
            <p className="text-sm text-gray-500 mt-2">
              {t('results.supervision.needAcceptance')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentTrainee = supervisedTrainees.find(t => t.user_id === selectedTrainee);
  const filteredResults = getFilteredResults();
  const level1Results = getResultsByLevel("1");
  const level2Results = getResultsByLevel("2");
  const level3Results = getResultsByLevel("3");
  const testTypes = getTestTypes();
  const chartDataByTest = getChartDataByTest();

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      
      <div className="container mx-auto p-4 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-golf-800 mb-2">
              {t('results.supervision.title')}
            </h1>
            <p className="text-gray-600">
              {t('results.supervision.description')}
            </p>
          </div>
          
          <div className="flex gap-4">
            <Select value={selectedTrainee} onValueChange={setSelectedTrainee}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder={t('results.supervision.selectTrainee')} />
              </SelectTrigger>
              <SelectContent>
                {supervisedTrainees.map((trainee) => (
                  <SelectItem key={trainee.user_id} value={trainee.user_id}>
                    {trainee.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLevel || "all"} onValueChange={(value) => setSelectedLevel(value === "all" ? null : value)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder={t('results.supervision.filterByLevel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('results.supervision.allLevels')}</SelectItem>
                <SelectItem value="1">{t('results.supervision.level1')}</SelectItem>
                <SelectItem value="2">{t('results.supervision.level2')}</SelectItem>
                <SelectItem value="3">{t('results.supervision.level3')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!selectedTrainee ? (
          <Card>
            <CardContent className="p-6 text-center text-gray-500">
              <p>{t('results.supervision.selectTraineePrompt')}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h2 className="font-semibold text-blue-800">
                {t('results.supervision.selectedTrainee')}: {currentTrainee?.username}
              </h2>
              {currentTrainee?.email && (
                <p className="text-sm text-blue-600">{currentTrainee.email}</p>
              )}
            </div>

            <Tabs defaultValue="overview" className="space-y-6">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">{t('training:results.tabs.overview')}</TabsTrigger>
                <TabsTrigger value="charts">{t('training:results.tabs.charts')}</TabsTrigger>
                <TabsTrigger value="details">{t('training:results.tabs.details')}</TabsTrigger>
                <TabsTrigger value="statistics">{t('training:results.tabs.statistics')}</TabsTrigger>
                <TabsTrigger value="course">{t('training:courseStats.title')}</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('training:results.supervision.cards.totalTests')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-golf-600">
                        {filteredResults.length}
                      </div>
                      <p className="text-sm text-gray-500">
                        {t('training:results.supervision.cards.testsCompleted')}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>{t('training:results.supervision.cards.exerciseTypes')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-golf-600">
                        {testTypes.length}
                      </div>
                      <p className="text-sm text-gray-500">
                        {t('training:results.supervision.cards.differentExercises')}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>{t('training:results.supervision.cards.mostPracticedLevel')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-golf-600">
                        {t('training:results.supervision.level')} {level3Results.length >= level2Results.length && level3Results.length >= level1Results.length ? "3" : 
                                level2Results.length >= level1Results.length ? "2" : "1"}
                      </div>
                      <p className="text-sm text-gray-500">
                        {t('training:results.supervision.cards.basedOnTestCount')}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="charts">
                <TrainingResultsChart 
                  chartDataByTest={chartDataByTest}
                  testTypes={testTypes}
                  selectedLevel={selectedLevel}
                  trainingResults={filteredResults}
                  swingTendencies={swingTendencies}
                  traineeUserId={selectedTrainee}
                />
              </TabsContent>

              <TabsContent value="details">
                <TrainingResultsTable 
                  results={filteredResults}
                  isCoach={isCoach}
                />
              </TabsContent>

              <TabsContent value="statistics">
                <TrainingStatistics 
                  results={filteredResults}
                  level1Results={level1Results}
                  level2Results={level2Results}
                  level3Results={level3Results}
                />
              </TabsContent>

              <TabsContent value="course">
                {courseStatsLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <RefreshCw className="h-8 w-8 animate-spin text-golf-600" />
                  </div>
                ) : courseStats.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground">{t('course-stats:noData')}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    <CourseStatsFilters 
                      filters={courseFilters}
                      setFilters={setCourseFilters}
                      courses={courses}
                    />
                    <PerformanceSummaryCards stats={courseStats} />
                    <AdvancedStatsCards stats={courseStats} />
                    <BestPerformanceSection stats={courseStats} />
                    <ScoreEvolutionChart stats={courseStats} />
                    <PrecisionEvolutionChart stats={courseStats} />
                    <RoundsHistoryTable stats={courseStats} />
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

export default TrainingResults;
